import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { useTranslation } from "react-i18next";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import swal from "sweetalert";
import {
  LoadingSpinner,
  customOption,
  customSingleValue,
  isUrdu,
} from "../../../../constants/const";
import { GetLanguageString } from "../../../helper/Components";
import { GetSelectedLanguage } from "../../../helper/Method";
import useForm from "../../../shared/useForm";
import Select from "react-select";
import { ApiFinance } from "../ApiFinance";

const EditFinanceAccountCategory = ({
  setEditModal,
  setReload,
  reload,
  accountCategoryValues,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [financeSubCategoryTypes, setFinanceSubCategoryTypes] = useState([]);
  const [selectedSubCategoryType, setSelectedSubCategoryType] = useState(null);
  const [selectedDim2CategoryType, setSelectedDim2CategoryType] = useState(null);
  const [errors, setErrors] = useState({
    category: "",
  });
  const { values, handleInputChange } = useForm(accountCategoryValues);
  const toggle = () => setEditModal(false);

  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleDrag = (e, ui) => {
    const { x, y } = position;
    setPosition({ x: x + ui.deltaX, y: y + ui.deltaY });
  };

  useEffect(() => {
    setLoading(true);
    ApiFinance()
      .GetFinanceSubCategoryTypes()
      .then((res) => {
        const options = res.data.map((item) => ({
          value: item.id,
          label: item.type,
        }));
        setFinanceSubCategoryTypes(options);
        setSelectedSubCategoryType(
          options.find(
            (item) =>
              item.value === values.subCategoryType?.value ||
              item.value === values.subCategoryTypeId
          )
        );
        setSelectedDim2CategoryType(
          options.find(
            (item) =>
              item.value === values.dim2CategoryType?.value ||
              item.value === values.dim2CategoryTypeId
          )
        );
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        swal({
          title: err,
          text: err.response.data,
          icon: "error",
          buttons: "OK",
        });
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  const validate = () => {
    let temp = {};
    temp.category = values.category ? "" : t("common_field_required_error");
    temp.subCategoryTypeId = selectedSubCategoryType
      ? ""
      : t("common_field_required_error");
    temp.dim2CategoryTypeId = selectedDim2CategoryType
      ? ""
      : t("common_field_required_error");
    setErrors({
      ...temp,
    });

    return Object.values(temp).every((x) => x === "");
  };

  const preparePayload = () => {
    const payload = { ...values };
    payload.code = payload.code ?? "";
    payload.subCategoryTypeId = selectedSubCategoryType.value;
    payload.subCategoryType = selectedSubCategoryType;
    payload.dim2CategoryTypeId = selectedDim2CategoryType.value;
    payload.dim2CategoryType = selectedDim2CategoryType;
    payload.isAttachmentRequired = !!values.isAttachmentRequired;
    payload.isSingleRowOnly = !!values.isSingleRowOnly;
    payload.allowDuplicateCategory = values.allowDuplicateCategory !== undefined ? !!values.allowDuplicateCategory : true;
    payload.isVendorRequired = !!values.isVendorRequired;
    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const payload = preparePayload();
    ApiFinance()
      .UpdateFinanceSubCategory(values.id, payload)
      .then(() => {
        swal({
          title: t("finance_finance_account_category_updated"),
          className: GetSelectedLanguage() === "en" ? "" : "ur",
          icon: "success",
          buttons: "OK",
        });
        setReload(!reload);
        toggle();
      })
      .catch((err) => {
        swal({
          title: err.message || "Something went wrong",
          text: err.response?.data || "",
          icon: "error",
          buttons: "OK",
        });
        toggle();
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <Draggable position={position} onDrag={handleDrag} cancel=".no-drag">
        <Modal isOpen={true} className="inmodal" autoFocus={false}>
          <ModalHeader className="modal-title" tag="h4">
            {
              <GetLanguageString props="finance_tab_finance_account_categories_edit" />
            }
          </ModalHeader>
          {!loading ? (
            <Form onSubmit={handleSubmit}>
              <ModalBody>
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="finance_tab_category" />}
                  </Label>
                  <Input
                    autoFocus
                    type="text"
                    name="category"
                    className={
                      isUrdu(values.category) ? "ur no-drag" : "no-drag"
                    }
                    value={values.category}
                    onChange={handleInputChange}
                    {...(errors.category && { invalid: true })}
                  />
                  {errors.category && (
                    <FormFeedback>
                      <h4>{errors.category}</h4>
                    </FormFeedback>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_tab_finance_sub_category_select_account_type" />
                    }
                  </Label>
                  <Select
                    components={{
                      Option: customOption,
                      SingleValue: customSingleValue,
                    }}
                    value={selectedSubCategoryType}
                    options={financeSubCategoryTypes}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedSubCategoryType(v);
                      setErrors({
                        ...errors,
                        subCategoryTypeId: "",
                      });
                    }}
                  />
                  {errors.subCategoryTypeId && (
                    <h4 className="text-error">{errors.subCategoryTypeId}</h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label className="col-form-label">
                    {
                      <GetLanguageString props="finance_tab_finance_sub_category_select_dim2_type" />
                    }
                  </Label>
                  <Select
                    components={{
                      Option: customOption,
                      SingleValue: customSingleValue,
                    }}
                    value={selectedDim2CategoryType}
                    options={financeSubCategoryTypes}
                    className="basic-single no-drag"
                    classNamePrefix="select"
                    onChange={(v) => {
                      setSelectedDim2CategoryType(v);
                      setErrors({
                        ...errors,
                        dim2CategoryTypeId: "",
                      });
                    }}
                  />
                  {errors.dim2CategoryTypeId && (
                    <h4 className="text-error">{errors.dim2CategoryTypeId}</h4>
                  )}
                </FormGroup>
                <FormGroup>
                  <Label>
                    {<GetLanguageString props="settings_code" /> || "Code"}
                  </Label>
                  <Input
                    type="text"
                    name="code"
                    className={isUrdu(values.code) ? "ur no-drag" : "no-drag"}
                    value={values.code}
                    onChange={handleInputChange}
                  />
                </FormGroup>
                <FormGroup check className="mb-2">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="isAttachmentRequired"
                      checked={!!values.isAttachmentRequired}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "isAttachmentRequired",
                            value: e.target.checked,
                          },
                        })
                      }
                      className="no-drag"
                    />{" "}
                    {<GetLanguageString props="finance_is_attachment_required" /> || "Attachment Required"}
                  </Label>
                </FormGroup>
                <FormGroup check className="mb-2">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="isSingleRowOnly"
                      checked={!!values.isSingleRowOnly}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "isSingleRowOnly",
                            value: e.target.checked,
                          },
                        })
                      }
                      className="no-drag"
                    />{" "}
                    {<GetLanguageString props="finance_is_single_row_only" /> || "Single Row Only"}
                  </Label>
                </FormGroup>
                <FormGroup check className="mb-2">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="allowDuplicateCategory"
                      checked={values.allowDuplicateCategory !== undefined ? !!values.allowDuplicateCategory : true}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "allowDuplicateCategory",
                            value: e.target.checked,
                          },
                        })
                      }
                      className="no-drag"
                    />{" "}
                    {<GetLanguageString props="finance_allow_duplicate_category" /> || "Allow Duplicate Selection"}
                  </Label>
                </FormGroup>
                <FormGroup check className="mb-2">
                  <Label check>
                    <Input
                      type="checkbox"
                      name="isVendorRequired"
                      checked={!!values.isVendorRequired}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "isVendorRequired",
                            value: e.target.checked,
                          },
                        })
                      }
                      className="no-drag"
                    />{" "}
                    {<GetLanguageString props="finance_is_vendor_required" /> || "Vendor Bank Account Required"}
                  </Label>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="white" onClick={toggle}>
                  {<GetLanguageString props="common_cancel" />}
                </Button>
                <Button color="primary" type="submit">
                  {<GetLanguageString props="common_save_changes" />}
                </Button>
              </ModalFooter>
            </Form>
          ) : (
            LoadingSpinner()
          )}
        </Modal>
      </Draggable>
    </div>
  );
};

export default EditFinanceAccountCategory;
